import { Dropdown, Modal } from 'react-bootstrap';

const UserTransactionLimitTypeModal = ({
  show,
  onHide,
  currentUser,
  formData,
  onInputChange,
  onSubmit,
  transactionLimitTypes = [],
  transactionLimitTypesLoading = false,
  existingTransactionLimits = [],
  existingTransactionLimitsLoading = false,
  isSubmitting = false,
  isRevoking = false,
  confirmRevoke,               // function to show confirm revoke modal
  showConfirmModal,            // boolean
  limitToRevoke,               // id or object
  setLimitToRevoke,            // function to set limit
  onCancelRevoke,              // function to close confirm modal
  onConfirmRevoke,              // function to perform actual revoke
  canManagePartnerDetails
}) => {
  if (!currentUser) return null;

  const isSubmitDisabled =
    isSubmitting ||
    !formData.amount_limit ||
    !formData.transaction_limit_type_id;

  return (
    <Modal
      show={show}
      onHide={onHide}
      className="modal fade"
      id="updateUserTransactionLimitModal"
      backdrop={isSubmitting || isRevoking ? 'static' : true}
      keyboard={!(isSubmitting || isRevoking)}
    >
      <>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{currentUser.preferred_name}'s Transaction Limits</h5>
            <button
              type="button"
              onClick={onHide}
              className="btn-close"
              data-dismiss="modal"
              disabled={isSubmitting || isRevoking}
            >
            </button>
          </div>
          <div className="modal-body" data-tour="user-limits-modal-body">
            <div className="mb-4" data-tour="user-limits-existing-section">
              <h6>Existing Limits</h6>
              <div className="row">
                {existingTransactionLimitsLoading ? (
                  <div className="d-flex justify-content-center align-items-center mb-4" style={{ minHeight: '100px' }}>
                    <div className="spinner-border text-primary" role="status" aria-hidden="true"></div>
                  </div>
                ) : (
                  existingTransactionLimits && existingTransactionLimits.length > 0 ? (
                    existingTransactionLimits.map((limit) => (
                      <div
                        key={limit.id}
                        className={`col-md-6 mb-3 position-relative`}
                      >
                        <div
                          className="border rounded p-3 h-100 position-relative"
                          style={{
                            backgroundColor:
                              limit.type === 'temporary'
                                ? 'rgba(255, 0, 0, 0.05)'    // light red
                                : limit.type === 'primary'
                                  ? 'rgba(0, 128, 0, 0.05)'   // light green
                                  : 'rgba(240, 240, 240, 0.4)' // fallback light gray
                          }}
                        >
                          {canManagePartnerDetails && (<button
                            type="button"
                            className="btn btn-sm btn-outline-danger position-absolute top-0 end-0 m-2 p-1"
                            aria-label={limit.type === 'temporary' ? 'Revert to original limit' : 'Remove limit'}
                            title={limit.type === 'temporary' ? 'Revert to original' : 'Remove limit'}
                            data-tour="user-limits-revoke-button"
                            disabled={isSubmitting}
                            onClick={() => {
                              setLimitToRevoke(limit);
                              confirmRevoke();
                            }}
                          >
                            <i className="fa fa-trash text-muted" aria-hidden="true"></i>
                          </button>)}
                          <div className="fw-semibold pe-4">
                            {limit.description}: <strong>{limit.amount_limit}</strong>
                          </div>
                          {limit.type === 'temporary' && limit.expires_on && (
                            <div className="mt-1 small text-danger">
                              Expires: {limit.expires_on}
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="col-12 text-center text-muted">No active limits found.</div>
                  )
                )}
              </div>
            </div>

            {canManagePartnerDetails && (<form
              onSubmit={(e) => {
                e.preventDefault();
                onSubmit();
              }}
              data-tour="user-limits-add-form"
            >
              <h6>Update Limits</h6>

              <div className="mb-3">
                <label className="form-label d-block">Limit Type</label>
                {(() => {
                  const selectedType = transactionLimitTypes?.find(
                    (type) => String(type.id) === String(formData.transaction_limit_type_id)
                  );
                  const currentLabel = selectedType
                    ? selectedType.description
                    : (transactionLimitTypesLoading ? 'Loading types...' : 'Select a limit type...');
                  return (
                    <Dropdown>
                      <Dropdown.Toggle
                        variant="outline-secondary"
                        size="sm"
                        className="d-inline-flex align-items-center"
                        style={{ minWidth: '140px' }}
                        disabled={transactionLimitTypesLoading}
                      >
                        <span className="flex-grow-1 text-start">{currentLabel}</span>
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        {transactionLimitTypes?.map((type) => (
                          <Dropdown.Item
                            key={type.id}
                            active={String(formData.transaction_limit_type_id) === String(type.id)}
                            onClick={() =>
                              onInputChange({
                                target: { id: 'transaction_limit_type_id', value: type.id },
                              })
                            }
                          >
                            {type.description}
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  );
                })()}
              </div>

              <div className="form-group mb-3">
                <label htmlFor="amount_limit">Amount Limit</label>
                <input
                  type="number"
                  className="form-control"
                  id="amount_limit"
                  placeholder=""
                  value={formData.amount_limit}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d*$/.test(value) && Number(value) <= 150000) {
                      onInputChange(e);
                    }
                  }}
                  onKeyDown={(e) => {
                    const invalidKeys = ['e', 'E', '.', '+', '-'];
                    if (invalidKeys.includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  min="0"
                  step="1"
                />
              </div>

              <button className="btn btn-primary btn-block" disabled={isSubmitDisabled}>
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Adding…
                  </>
                ) : 'Add'}
              </button>
            </form>)}
          </div>
        </div>
      </>

      <Modal show={showConfirmModal} onHide={onCancelRevoke}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {limitToRevoke?.type === 'temporary' ? 'Revert to Original Limit' : 'Remove Limit'}
            </h5>
            <button type="button" className="btn-close" onClick={onCancelRevoke} disabled={isRevoking}></button>
          </div>
          <div className="modal-body">
            {limitToRevoke?.type === 'temporary' ? (
              <p className="mb-0">
                This will cancel the temporary extension and restore the original limit of{' '}
                <strong>
                  {limitToRevoke.original_amount_limit != null
                    ? limitToRevoke.original_amount_limit
                    : 'no set limit'}
                </strong>.
              </p>
            ) : (
              <>
                <div className="alert alert-danger mb-3">
                  <strong>Warning:</strong> This will permanently remove the limit.{' '}
                  <strong>{limitToRevoke?.user}</strong> will have no spending limit and will be able to spend any amount.
                </div>
                <p className="mb-0 text-muted small">
                  If you only need a short-term change, consider closing this and setting a temporary limit instead.
                </p>
              </>
            )}
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onCancelRevoke} disabled={isRevoking}>Cancel</button>
            <button className="btn btn-danger" onClick={() => onConfirmRevoke(limitToRevoke)} disabled={isRevoking}>
              {isRevoking ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  {limitToRevoke?.type === 'temporary' ? 'Reverting…' : 'Removing…'}
                </>
              ) : (
                limitToRevoke?.type === 'temporary' ? 'Revert' : 'Remove Limit'
              )}
            </button>
          </div>
        </div>
      </Modal>
    </Modal>
  );
};

export default UserTransactionLimitTypeModal;